/* Dashboard — placeholder overview cards.
 *
 * Phase 3.1 ships a SHELL. The real overview page (with rolling 24h
 * fleet aggregates, top noisy devices, alert heatmap, etc.) is Phase
 * 3.2 work driven by the analytics route port from KAIOT. */

import { Card, Pill } from '@sp/design-system-react';
import { useDevices } from '../hooks/useDevices';
import { useAlerts } from '../hooks/useAlerts';

export function DashboardPage(): JSX.Element {
  const devices = useDevices();
  const alerts = useAlerts({ includeResolved: false });

  const onlineCount = devices.data?.filter((d) => d.last_seen_at !== null).length ?? 0;
  const totalCount = devices.data?.length ?? 0;
  const openAlerts = alerts.data?.length ?? 0;
  const alarmCount = alerts.data?.filter((a) => a.severity === 'alarm').length ?? 0;
  const warnCount = alerts.data?.filter((a) => a.severity === 'warn').length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-h1" style={{ color: 'var(--ink-primary)' }}>
          Overview
        </h1>
        <span className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
          Air Quality vertical · live
        </span>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card variant="surface" density="default">
          <div className="flex flex-col gap-2">
            <span className="text-label" style={{ color: 'var(--ink-tertiary)' }}>
              Devices online
            </span>
            <span className="text-data-lg" style={{ color: 'var(--ink-primary)' }}>
              {onlineCount}
              <span className="text-body" style={{ color: 'var(--ink-tertiary)' }}>
                {' / '}
                {totalCount}
              </span>
            </span>
          </div>
        </Card>

        <Card variant="surface" density="default">
          <div className="flex flex-col gap-2">
            <span className="text-label" style={{ color: 'var(--ink-tertiary)' }}>
              Open alerts
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-data-lg" style={{ color: 'var(--ink-primary)' }}>
                {openAlerts}
              </span>
              {alarmCount > 0 && <Pill tone="alarm" label={`${alarmCount} alarm`} size="sm" />}
              {warnCount > 0 && <Pill tone="warn" label={`${warnCount} warn`} size="sm" />}
            </div>
          </div>
        </Card>

        <Card variant="surface" density="default">
          <div className="flex flex-col gap-2">
            <span className="text-label" style={{ color: 'var(--ink-tertiary)' }}>
              Status
            </span>
            <div className="flex items-center gap-2">
              <Pill
                tone={devices.isError || alerts.isError ? 'alarm' : 'ok'}
                label={devices.isError || alerts.isError ? 'Backend error' : 'All systems operational'}
                size="sm"
              />
            </div>
          </div>
        </Card>
      </section>

      <Card variant="sunken" density="default">
        <p className="text-body" style={{ color: 'var(--ink-secondary)' }}>
          This is the Phase 3.1 shell. The full KAIOT-equivalent dashboards (analytics,
          AHU monitoring, compliance, FDD, floor plans, heatmap) will be ported in Phase
          3.2 once the vertical-aq plugin ships those routes.
        </p>
      </Card>
    </div>
  );
}
